import React, { useContext, useReducer, useEffect, useCallback } from 'react';
import SlideModal, { SlideModalProps } from '../components/@common/SlideModal';
import { useHistory } from 'react-router-dom';
import {
  makeActionRouteParams,
  getActionKey,
  makeModalRouteAction,
  getModalActionKey,
  RouteParams
} from 'hooks/route-action/util';
import { ModalCloseOptions } from 'types';

type ModalOptions = Pick<
  SlideModalProps,
  'containerClass' | 'hideCloseButton' | 'hideHeader' | 'paperProps' | 'unmountOnClose'
>;

interface OpenModalPayload {
  body: JSX.Element;
  title?: string | null;
  routeAction?: string | null;
  routeParams?: RouteParams;
  options?: ModalOptions;
  handleAfterClose?: VoidFunction;
  handleOnClose?: (closeOptions?: ModalCloseOptions) => void;
}

type OpenModalDispatch = OpenModalPayload & { routeWhenModalOpened: string };

type Action =
  | { type: 'OPEN_QUARTER_MODAL'; payload: OpenModalDispatch }
  | { type: 'OPEN_HALF_MODAL'; payload: OpenModalDispatch }
  | { type: 'OPEN_FULL_MODAL'; payload: OpenModalDispatch }
  | { type: 'OPEN_FLOATING_MODAL'; payload: OpenModalDispatch }
  | { type: 'ROUTE_ACTION_CHANGING'; payload: boolean }
  | { type: 'PERSIST_ROUTE_WHEN_MODAL_OPENED'; payload: string }
  | { type: 'CLOSE_MODAL' };

export enum ModalVariant {
  QUARTER = 'quarter',
  HALF = 'half',
  FULL = 'full',
  FLOATING = 'floating'
}

export type OpenModalHandler = (payload: OpenModalPayload) => void;
export type CloseModalHandler = () => void;
export type UseModal = [UseModalState, UseModalDispatch];
export interface UseModalState {
  isOpen: boolean;
  isRouteActionChanging: boolean;
  variant: ModalVariant;
  title: string | null;
  body: JSX.Element | null;
  handleOnClose?: VoidFunction;
  handleAfterClose?: VoidFunction | undefined;
  routeAction?: string | null;
  routeParams?: RouteParams;
  routeWhenModalOpened: string | null;
  options?: ModalOptions;
}
export interface UseModalDispatch {
  openQuarterModal: (payload: OpenModalPayload) => void;
  openHalfModal: (payload: OpenModalPayload) => void;
  openFullModal: (payload: OpenModalPayload) => void;
  openFloatingModal: (payload: OpenModalPayload) => void;
  closeModal: () => void;
}

export const getDefaultModalState = (): UseModalState => ({
  isOpen: false,
  isRouteActionChanging: false,
  routeAction: null,
  routeWhenModalOpened: null,
  variant: ModalVariant.HALF,
  title: null,
  body: null,
  options: getDefaultModalOptions()
});

const getDefaultModalOptions = (): ModalOptions => ({
  hideHeader: true,
  unmountOnClose: true
});

const resolvePayload = (payload: OpenModalPayload): OpenModalPayload => {
  const { options, ...updates } = payload;
  const modalOptions = { ...getDefaultModalOptions(), ...options };

  return { ...updates, options: modalOptions };
};

const modalReducer = (state: UseModalState, action: Action): UseModalState => {
  switch (action.type) {
    case 'OPEN_QUARTER_MODAL':
      return {
        ...state,
        ...resolvePayload(action.payload),
        variant: ModalVariant.QUARTER,
        isOpen: true
      };
    case 'OPEN_HALF_MODAL':
      return {
        ...state,
        ...resolvePayload(action.payload),
        variant: ModalVariant.HALF,
        isOpen: true
      };
    case 'OPEN_FULL_MODAL':
      return {
        ...state,
        ...resolvePayload(action.payload),
        variant: ModalVariant.FULL,
        isOpen: true
      };
    case 'OPEN_FLOATING_MODAL':
      return {
        ...state,
        ...resolvePayload(action.payload),
        variant: ModalVariant.FLOATING,
        isOpen: true
      };
    case 'ROUTE_ACTION_CHANGING':
      return {
        ...state,
        isRouteActionChanging: action.payload
      };
    case 'PERSIST_ROUTE_WHEN_MODAL_OPENED':
      return {
        ...state,
        routeWhenModalOpened: action.payload
      };
    case 'CLOSE_MODAL':
      return getDefaultModalState();
    default:
      return getDefaultModalState();
  }
};

/**
 * UseModal should be used to control modal state and UI display
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ModalContext = React.createContext<any>([]);

const ModalProvider: React.FC = props => {
  const history = useHistory();
  const [state, dispatch] = useReducer<React.Reducer<UseModalState, Action>>(
    modalReducer,
    getDefaultModalState()
  );

  const getRoute = (routeAction: string | null, routeParams: RouteParams = {}): string => {
    let route = window.location.href;

    if (routeAction && !window.location.search.includes(routeAction)) {
      route = `${window.location.pathname}${makeActionRouteParams({
        locationSearch: window.location.search,
        action: routeAction,
        routeParams,
        actionKeyFn: makeModalRouteAction,
        actionParamKeyFn: getActionKey
      })}`;
    }

    return route;
  };

  const maybeNavigate = useCallback(
    (route: string) => {
      if (route !== window.location.href) history.push(route);
    },
    [history]
  );

  const openQuarterModal = ({
    routeAction = null,
    routeParams,
    ...payload
  }: OpenModalPayload): void => {
    const route = getRoute(routeAction, routeParams);

    dispatch({
      type: 'OPEN_QUARTER_MODAL',
      payload: { ...payload, routeAction, routeWhenModalOpened: route }
    });

    maybeNavigate(route);
  };

  const openHalfModal = ({
    routeAction = null,
    routeParams,
    ...payload
  }: OpenModalPayload): void => {
    const route = getRoute(routeAction, routeParams);

    dispatch({
      type: 'OPEN_HALF_MODAL',
      payload: { ...payload, routeAction, routeWhenModalOpened: route }
    });

    maybeNavigate(route);
  };

  const openFullModal = ({
    routeAction = null,
    routeParams,
    ...payload
  }: OpenModalPayload): void => {
    const route = getRoute(routeAction, routeParams);

    dispatch({
      type: 'OPEN_FULL_MODAL',
      payload: { ...payload, routeAction, routeWhenModalOpened: route }
    });

    maybeNavigate(route);
  };

  const openFloatingModal = ({
    routeAction = null,
    routeParams,
    ...payload
  }: OpenModalPayload): void => {
    const route = getRoute(routeAction, routeParams);

    dispatch({
      type: 'OPEN_FLOATING_MODAL',
      payload: { ...payload, routeAction, routeWhenModalOpened: route }
    });

    maybeNavigate(route);
  };

  const closeModal = useCallback((): void => {
    if (state.handleOnClose) {
      state.handleOnClose();
    }

    if (window.location.search.includes(getModalActionKey())) {
      dispatch({ type: 'ROUTE_ACTION_CHANGING', payload: true });
      /* 'replacing' instead of 'pushing' for now */
      history.replace(
        `${window.location.pathname}${makeActionRouteParams({
          locationSearch: window.location.search
        })}`
      );
      dispatch({ type: 'ROUTE_ACTION_CHANGING', payload: false });
    }
    dispatch({ type: 'CLOSE_MODAL' });

    if (state.handleAfterClose) {
      state.handleAfterClose();
    }
  }, [state, dispatch, history]);

  useEffect(() => {
    const unsubscribe = history.listen(() => {
      if (state.isOpen && window.location.href !== state.routeWhenModalOpened) {
        closeModal();
      }
    });
    return (): void => {
      unsubscribe();
    };
  }, [history, state.routeWhenModalOpened, state.isOpen, closeModal]);

  return (
    <ModalContext.Provider
      value={[
        state,
        { openQuarterModal, openHalfModal, openFullModal, openFloatingModal, closeModal }
      ]}
    >
      <SlideModal
        {...state.options}
        variant={state.variant}
        onClose={closeModal}
        open={state.isOpen}
      >
        {state.body}
      </SlideModal>
      {props.children}
    </ModalContext.Provider>
  );
};

const useModal = (): UseModal => {
  const [state, dispatch] = useContext(ModalContext);

  return [state, dispatch];
};

export { ModalProvider, useModal };
